import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import { isEqual as _isEqual } from 'lodash';

import { useTheme } from '@mui/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';

import RefreshIcon from '@mui/icons-material/Refresh';
import FrownIcon from '@mui/icons-material/SentimentDissatisfiedSharp';
import SmileIcon from '@mui/icons-material/SentimentSatisfiedSharp';

import SearchBar from '../../../components/searchbar';
import CellExpand from '../../../components/xgrid/cell-expand';

import IncidentService, { LogFields } from '../../../data/incident-service';
import { ProfileContext } from '../../../data/auth-service';

import useWindowSize from '../../../libraries/usewindowsize';
import Helpers from '../../../libraries/helpers';


const IncidentLogs = (props) => {
    const theme = useTheme();
    const [logs, setLogs] = useState([]);
    const [columnWidth, setColumnWidth] = useState({});
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [sortModel, setSortModel] = useState([{field: 'created', sort: 'desc'}]);
    const logsAPIRef = useApiRef();
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const { height } = useWindowSize();

    useEffect(() => {
        setColumnWidth(getProfile('incident_logs').widths || {});
    }, []);

    useEffect(() => {
        getLogs();
    }, [props.incident, search]);

    const handleColumnResize = () => {
        const widths = logsAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});
        updateProfile('incident_logs', {
            ...getProfile('incident_logs'),
            widths: widths,
        });
        setColumnWidth(widths);
    };

    useEffect(() => {
        setLoading(false);
    }, [logs]);

    const getLogs = () => {
        IncidentService.getLogs(props.incident.uuid).then(res => {
            if (!res) {
                setLogs([]);
            } else {
                res = res.map(v => {
                    return {
                        ...v,
                        id: Helpers.UniqueID()
                    };
                }).filter(v => {
                    return Object.keys(v).reduce((a, c) => {
                        if (a || (c === 'created' && Moment(v[c]).format(theme.date.long).includes(search)) || v[c].includes(search)) {
                            return true;
                        }
                        return false;
                    }, false);
                });
                setLogs(res);
            }
        }, err => {
            console.error(err);
        });
    };

    const handleSearchChange = s => {
        setSearch(s.search);
    };

    const handleSortUpdate = s => {
        if (!_isEqual(s, sortModel)) {
            setSortModel(s);
        }
    };

    /* eslint-disable react/display-name */
    const tableRenderers = {
        created: d => { return <div>{Moment(d.row.created).format(theme.date.long)}</div> },
        host: d => { return <div>{d.row.host}</div> },
        actor: d => { return <div>{d.row.actor}</div> },
        action: d => { return <CellExpand { ...{ ...d, value: d.row.action } }/> },
        result: d => { return d.row.result === 'fail' ? <FrownIcon style={{ color: theme.palette.error.main }} /> : <SmileIcon style={{ color: theme.palette.success.main }} /> },
    };

    const columns = LogFields.map(f => {
        return {
            ...f,
            sortable: true,
            width: columnWidth[f.title] || 250,
            ...(!columnWidth[f.title] ? { flex: f.flexDefault || 1 } : {}),
            headerName: f.title,
            valueGetter: (p) => { return p.row[f.variable] },
            type: 'string',
            disableColumnMenu: true,
            hidden: false,
            renderCell: tableRenderers[f.variable],
        };
    });

    return (
        <Box mt={2} width="100%" height={height - 460}>
            <Box mb={2} display="flex" justifyContent="space-between">
                <SearchBar immediate onSearch={handleSearchChange} searchservice={IncidentService} />
                <Box mt={1}><Button variant="contained" color="primary" onClick={getLogs}>
                    <RefreshIcon /> Refresh
                </Button></Box>
            </Box>
            <DataGridPro
                apiRef={logsAPIRef}
                rows={logs}
                columns={columns}
                loading={loading}
                sortModel={sortModel}
                onSortModelChange={handleSortUpdate}
                disableMultipleSelection
                disableMultipleColumnsSorting
                disableColumnFilter
                disableColumnReorder
                disableColumnMenu
                onColumnResize={handleColumnResize}
                // onSortModelChange={handleUpdateSort}
            />
        </Box>
    );
};

IncidentLogs.propTypes = {
    incident: PropTypes.object.isRequired,
    onUpdate: PropTypes.func,
    onRemoveData: PropTypes.func,
};

export default IncidentLogs;
